// The director bus: one object, recomputed once per frame by scene-manager
// and attached as profile.director, so a visualizer that wants coherent
// cross-layer intensity/pulse timing has ONE place to read it instead of
// each reimplementing its own progressIntensity() (see the diagnostic this
// whole refactor started from). Pure computation - no THREE/DOM here,
// scene-manager.js is the only caller and the only place touching the
// renderer/overlay directly.
import { computeBeatPulse } from './beat-pulse.js';
import { safeZoneUniforms } from './shaders/safeZone.glsl.js';
import { BEATS_PER_PHRASE } from './beat-grid.js';
import { resolveDanceabilityIndex } from './sonos-visual-profile.js';

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

const REFERENCE_BPM = 120;
const TEMPO_SCALE_MIN = 0.6;
const TEMPO_SCALE_MAX = 1.8;

// CHANTIER 6 (revision) - internal per-phrase variation, so a visualizer
// can mark the phrase's own accent WITHOUT the composition itself
// changing (see adaptive-party.js's now much longer composition
// durations - without something like this, a composition that just sits
// there for 20+ seconds reads as static). 1 for the last 2 beats of every
// 16-beat phrase, 0 otherwise - a sustained window, not a single-frame
// pulse, so a visualizer can either fire once on the RISING edge (see
// laser-cathedral.js/kaleido-drift.js/type-slam.js) or use the level
// directly.
const ACCENT_PHRASE_WINDOW_BEATS = 2;

const NEUTRAL_BANDS = { low: 0, mid: 0, high: 0 };

// Next known analysis mark ahead of the current position - a PURE lookup
// (the whole timeline is known in advance, see track-analysis.js's own
// header), unlike pushIntensity/preRollIntensity below which need to
// remember state ACROSS frames (has this mark already fired, how far into
// its own decay) and are therefore owned/mutated by adaptive-party.js
// after this object already exists, the exact same pattern the pre-roll
// intensity boost already uses for profile.director.intensity.
function computeNextEvent(timeline, nowMs) {
  if (!timeline) return null;
  const marks = timeline.marks;
  for (let i = 0; i < marks.length; i++) {
    if (marks[i].atMs > nowMs) {
      return { type: marks[i].type, score: marks[i].score, msUntil: marks[i].atMs - nowMs };
    }
  }
  return null;
}

// CHANTIER 4 - synthetic arrangement. A named 6-segment envelope rather
// than a bare sin() - intro/build/drop/breakdown/sustain/outro, in that
// order, each a fraction of the full 0..1 progress range. mode (see
// computeDirector) is just the name of whichever segment produced the
// current intensity value, so the two can never disagree with each other -
// one function produces both.
const SEGMENTS = [
  { name: 'intro', end: 0.08 },
  { name: 'build', end: 0.20 },
  { name: 'drop', end: 0.45 },
  { name: 'breakdown', end: 0.65 },
  { name: 'sustain', end: 0.90 },
  { name: 'outro', end: 1.0 }
];
// Value the envelope reaches at the END of each segment - the shape the
// brief describes: "intro basse, montee, plateau, respiration vers 65%,
// plateau final, sortie". Interpolated linearly between these anchors
// within a segment, continuous across segment boundaries by construction
// (each segment starts exactly where the previous one ended).
const ANCHORS = [0.30, 0.30, 0.95, 0.95, 0.65, 0.85, 0.85, 0.20];
// ^ paired with segment boundaries at progress = [0, 0.08, 0.08, 0.20, 0.20, 0.45, 0.45, 0.65, 0.65, 0.90, 0.90, 1.0]
// built out programmatically below instead of hand-aligning two arrays.
const ENVELOPE_POINTS = [
  { p: 0.00, v: 0.30 }, // intro starts low
  { p: 0.08, v: 0.30 }, // intro holds
  { p: 0.20, v: 0.95 }, // build rises into the drop
  { p: 0.45, v: 0.95 }, // drop plateau
  { p: 0.65, v: 0.65 }, // breakdown dips toward 65%
  { p: 0.90, v: 0.85 }, // sustain, a second (slightly lower) plateau
  { p: 1.00, v: 0.20 }  // outro falls away
];

function segmentAt(progress01) {
  const p = clamp01(progress01);
  for (let i = 0; i < SEGMENTS.length; i++) {
    if (p <= SEGMENTS[i].end || i === SEGMENTS.length - 1) return SEGMENTS[i].name;
  }
  return SEGMENTS[SEGMENTS.length - 1].name;
}

function envelopeValueAt(progress01) {
  const p = clamp01(progress01);
  for (let i = 1; i < ENVELOPE_POINTS.length; i++) {
    const a = ENVELOPE_POINTS[i - 1], b = ENVELOPE_POINTS[i];
    if (p <= b.p) {
      const t = b.p > a.p ? (p - a.p) / (b.p - a.p) : 0;
      return lerp(a.v, b.v, t);
    }
  }
  return ENVELOPE_POINTS[ENVELOPE_POINTS.length - 1].v;
}

// energy/danceability (0..100 or null) bias the envelope's overall level
// toward the track's own real intensity when TheAudioDB actually has data
// for it - renormalized across only the axes present, same "no arbitrary
// default" rule orchestrator.js's own scoreVisualization already follows,
// so a track with no data at all just plays the envelope shape unweighted.
export function computeIntensityEnvelope(progress01, energy, danceability) {
  const base = envelopeValueAt(progress01);
  const segment = segmentAt(progress01);
  const weights = [];
  if (energy != null) weights.push(energy / 100);
  if (danceability != null) weights.push(danceability / 100);
  if (weights.length === 0) return { value: clamp01(base), segment: segment };
  const trackLevel = weights.reduce(function (s, w) { return s + w; }, 0) / weights.length;
  // The envelope SHAPE always drives segment timing; trackLevel only pulls
  // the overall level up or down (blended 50/50) rather than replacing the
  // shape outright - a high-energy track should still have a quieter intro.
  return { value: clamp01(lerp(base, base * (0.5 + trackLevel), 0.5)), segment: segment };
}

// CHANTIER 4 - radio fix. duration===0 (web radio - see
// sonos-visual-profile.js) means profile.progress is permanently 0, which
// used to leave every progressIntensity() at its floor forever. A radio
// stream has no real "position in the track" to speak of, so this
// fabricates one: a fixed-length loop timed from when the current
// title/artist last changed, cycling the SAME envelope shape rather than
// leaving intensity flat.
const RADIO_LOOP_SECONDS = 210;

function syntheticProgress(profile) {
  const elapsedMs = Date.now() - profile.trackChangedAt;
  return (elapsedMs / 1000 / RADIO_LOOP_SECONDS) % 1;
}

// context: { qualityTier, frameMs } from the performance governor (see
// scene-manager.js - CHANTIER 7 populates these for real; until then they
// carry simple placeholders), and driftTime, scene-manager's own
// always-advancing-unless-paused clock (reused here rather than a new
// timer, so director.pulse()'s no-BPM breathing and _applyCameraDrift's
// own drift share one clock instead of two that could fall out of sync).
export function computeDirector(profile, context) {
  const progress = profile.duration > 0 ? profile.progress : syntheticProgress(profile);
  const energy = profile.trackFeatures ? profile.trackFeatures.energy : null;
  // L'indice du service (0-1), sur l'échelle 0-100 de l'enveloppe — la même
  // dansabilité partout, pas celle de TheAudioDB.
  const danceability = resolveDanceabilityIndex(profile) * 100;
  const envelope = computeIntensityEnvelope(progress, energy, danceability);

  const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
  const tempoScale = bpm ? clamp(bpm / REFERENCE_BPM, TEMPO_SCALE_MIN, TEMPO_SCALE_MAX) : 1.0;

  const driftTime = context.driftTime;
  const beatPhase = profile.beatPhase;

  const accentPhrase = profile.phrasePhase == null
    ? null
    : (profile.phrasePhase >= 1 - ACCENT_PHRASE_WINDOW_BEATS / BEATS_PER_PHRASE ? 1 : 0);

  // Real analysis timeline (radiopi /analyze) present - see
  // track-analysis.js's buildAnalysisTimeline. nowMs is null (and every
  // lookup below stays at its neutral default) whenever there is no
  // position to look anything up at, same "absent means absent" as
  // everywhere else in this pipeline.
  const timeline = profile.analysisTimeline;
  const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
  const bands = (timeline && nowMs != null) ? timeline.lookupBandEnvelope(nowMs) : NEUTRAL_BANDS;
  const bandPeak = (timeline && nowMs != null) ? timeline.lookupBandPeak(nowMs) : NEUTRAL_BANDS;
  const nextEvent = nowMs != null ? computeNextEvent(timeline, nowMs) : null;
  // structure.intro/outro/segments[].level, real and precise, replace the
  // synthetic progress-based envelope's own segment name whenever a
  // timeline exists - the envelope's VALUE (intensity) still comes from
  // that same synthetic shape either way (see computeIntensityEnvelope);
  // only which named segment we're calling this instant gets upgraded.
  const mode = (timeline && nowMs != null) ? timeline.lookupMode(nowMs) : envelope.segment;

  return {
    intensity: envelope.value,
    mode: mode,
    // Additive on-the-beat pushes for marks too weak to warrant a swap
    // (Phase 3) and pre-roll ramps ahead of a known strong drop/fall
    // (Phase 4) - both 0 by default, mutated onto THIS object by
    // adaptive-party.js after it already exists, same established pattern
    // as the pre-roll intensity boost a few lines below in that file.
    pushIntensity: 0,
    preRollIntensity: 0,
    // One frame wide - mutated true by adaptive-party.js's _handleMark on
    // the exact frame a "montée faible" mark fires, so a Phase 5
    // visualizer can react once without the composition itself changing.
    // Recomputed fresh (false) every frame here, same reset-by-
    // construction pattern as flash above.
    internalCue: false,
    bands: bands,
    bandPeak: bandPeak,
    nextEvent: nextEvent,
    // Whether beatPhase/barPhase/phrasePhase come from a real MEASURED
    // grid (radiopi's beat_times_ms) rather than a constant-tempo
    // extrapolation or nothing at all - resonance.js is the first
    // consumer, deliberately built to render identically well either way
    // (its whole reason for existing is unmetered acoustic material,
    // where there often IS no reliable grid).
    hasReliableGrid: !!timeline,
    // Static per-track character - see track-analysis.js's own
    // buildAnalysisTimeline (dynamics from loudness.dynamic_range_db,
    // percussiveness from onset density). Both fields null (not a
    // guessed default) without a timeline - same "absent means absent"
    // rule as bpm/energy elsewhere in this pipeline.
    character: timeline ? timeline.character : { dynamics: null, percussiveness: null, syncopation: null },
    beat: profile.beatPhase,
    bar: profile.barPhase,
    phrase: profile.phrasePhase,
    accent: profile.accent,
    accentPhrase: accentPhrase,
    confidence: profile.beatConfidence,
    tempoScale: tempoScale,
    // One frame wide - see adaptive-party.js's FLASH transition (chantier
    // 6), which sets profile.director.flash = 1 AFTER this object already
    // exists for the frame, and scene-manager's own render loop uploads it
    // to the filmic pass's uFlash right after. Recomputed fresh (0) every
    // frame here, so nothing has to remember to reset it.
    flash: 0,
    qualityTier: context.qualityTier,
    frameMs: context.frameMs,
    pulse: function (amount, decay) {
      return computeBeatPulse(beatPhase, amount, decay, driftTime);
    },
    uAspect: safeZoneUniforms.uAspect.value,
    uSafeCenter: { x: safeZoneUniforms.uSafeCenter.value.x, y: safeZoneUniforms.uSafeCenter.value.y },
    uSafeHalfSize: { x: safeZoneUniforms.uSafeHalfSize.value.x, y: safeZoneUniforms.uSafeHalfSize.value.y },
    uSafeFeather: safeZoneUniforms.uSafeFeather.value,
    // R19: the centre of composition, always the centre of the screen, and
    // the cover block's size as if centred - see shaders/safeZone.glsl.js.
    uStageCenter: { x: safeZoneUniforms.uStageCenter.value.x, y: safeZoneUniforms.uStageCenter.value.y },
    uStageHalfSize: { x: safeZoneUniforms.uStageHalfSize.value.x, y: safeZoneUniforms.uStageHalfSize.value.y }
  };
}
